import {
  FC,
  Fragment,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { LoadIndicator } from 'devextreme-react'
import {
  CrewAvatar,
  CrewAvatarSize,
} from 'components/elements/crewAvatar/crewAvatar'
import { generateImageAvatarUrl } from '@crew/utils/avatar'
import { EntityType } from '@crew/enums/domain'
import { useParams } from 'react-router-dom'
import { CrewFavorite } from 'components/elements/crewFavorite/crewFavorite'
import { useTranslation } from '@crew/modules/i18n'
import { CrewNotificationProject } from 'components/elements/crewNotificationProject/crewNotificationProject'
import { CrewConfirmDialog } from 'components/elements/crewConfirmDialog/crewConfirmDialog'
import { GroupChatAddMemberDialog } from 'features/groupChat/components/groupChatAddMemberDialog/groupChatAddMemberDialog'
import { useModal } from 'components/layouts/modal/useModal'
import { useAppSelector } from 'states/hooks'
import { useShowApiErrors } from 'hooks/useShowApiErrors'
import { useCrewNavigate } from 'hooks/useCrewNavigate'
import { useToast } from 'hooks/useToast'
import { useGroupChatHeadPanel } from './useGroupChatHeadPanel'
import {
  CrewGroupAvatar,
  DISPLAY_LIMIT,
} from 'components/elements/crewGroupAvatar/crewGroupAvatar'
import { useProjectPermissions } from '@crew/hooks'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import { Menu, Transition } from '@headlessui/react'
import classNames from 'classnames'
import MoreHoriz from '~icons/material-symbols/more-horiz'
import { GroupChat } from '../../groupChatPanel'
import { GroupChatEditDialog } from 'features/groupChat/components/groupChatEditDialog/groupChatEditDialog'

export type GroupChatHeadPanelProps = {
  groupChat: GroupChat | null | undefined
  isLoadingGroupChat: boolean
  isErrorGroupChat: boolean
  isJoinedGroupChat: boolean
  onDeletedGroupChat: () => void
}

// アバターを表示させるための最小パネル幅
const MIN_PANEL_WIDTH_SHOW_FULL_AVATAR = 500

export const GroupChatHeadPanel: FC<GroupChatHeadPanelProps> = memo((props) => {
  const { t } = useTranslation()
  const { projectId } = useParams()
  const { navigate } = useCrewNavigate()
  const { success } = useToast()
  const [showApiErrors] = useShowApiErrors()

  const loggedInUser = useAppSelector((state) => state.app.loggedInUser)
  const [displayAvatarLimit, setDisplayAvatarLimit] = useState(DISPLAY_LIMIT)
  const headPanelRef = useRef<HTMLDivElement>(null)

  // ResizeObserver でヘッダーパネルのサイズ変化を監視
  useEffect(() => {
    const node = headPanelRef.current
    if (!node) return

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // ヘッダーパネルが最小幅より小さいかどうかをチェック
        if (entry.contentRect.width < MIN_PANEL_WIDTH_SHOW_FULL_AVATAR) {
          //小さい場合は、`displayAvatarLimit`を0に設定してアバターを非表示にする
          setDisplayAvatarLimit(0)
        } else {
          // そうでない場合は、`displayAvatarLimit`をデフォルト値（DISPLAY_LIMIT）に設定する
          setDisplayAvatarLimit(DISPLAY_LIMIT)
        }
      }
    })
    // `node`のサイズの変化を監視するための`ResizeObserver`を作成
    resizeObserver.observe(node)

    return () => {
      // クリーンアップ関数: コンポーネントがアンマウントされるときに`node`の監視を解除する
      resizeObserver.disconnect()
    }
  }, [])

  // プロジェクトの権限を取得
  const { hasPrjProjectEditPermission, hasPrjMemberAddPermission } =
    useProjectPermissions(EntityType.Project, projectId)

  const { leaveGroupChat, isLoadingLeaveGroupChat, insertGroupMembers } =
    useGroupChatHeadPanel()

  // ヘッダー編集ダイアログ用
  const [
    isGroupChatEditDialogOpen,
    openGroupChatEditDialog,
    closeGroupChatEditDialog,
  ] = useModal()

  // メンバー追加ダイアログ用
  const [
    isGroupChatAddMemberDialogOpen,
    openGroupChatAddMemberDialog,
    closeGroupChatAddMemberDialog,
  ] = useModal()

  // 確認ダイアログ用
  const [isConfirmDialogOpen, openConfirmDialog, closeConfirmDialog] =
    useModal()
  const [confirmMessage, setConfirmMessage] = useState('')

  // グループチャット更新後の処理
  const handleGroupChatUpdated = useCallback(
    (projectId: string) => {
      // ダイアログを閉じる
      closeGroupChatEditDialog()
    },
    [closeGroupChatEditDialog]
  )

  // ユーザー追加処理
  const handleAddUsers = useCallback(
    async (userIds: string[]) => {
      if (!projectId || userIds.length === 0) return
      try {
        // プロジェクトメンバー追加処理
        await insertGroupMembers(projectId, userIds)

        success(t('message.groupChat.groupChatMemberRegistered'))
      } catch (error) {
        showApiErrors(error)
      }
    },
    [insertGroupMembers, projectId, showApiErrors, success, t]
  )

  // グループチャットから退出ボタンクリック時
  const handleLeaveGroupChatButtonClick = useCallback(() => {
    setConfirmMessage(t('message.groupChat.confirmLeaveGroupChat'))
    openConfirmDialog()
  }, [t, setConfirmMessage, openConfirmDialog])

  // グループチャットから退出する処理
  const handleLeaveGroupChatPermitButtonClick = useCallback(async () => {
    if (projectId && loggedInUser) {
      try {
        // プロジェクトから退出する
        await leaveGroupChat(projectId, loggedInUser?.id)

        success(t('message.groupChat.leaveGroupChatSuccess'))

        // プロジェクト一覧に遷移
        navigate('/projects')
      } catch (error) {
        showApiErrors(error)
      }
    }
    // 確認ダイアログを閉じる
    closeConfirmDialog()
  }, [
    projectId,
    loggedInUser,
    closeConfirmDialog,
    leaveGroupChat,
    success,
    t,
    navigate,
    showApiErrors,
  ])

  if (props.isErrorGroupChat) {
    return null
  }
  if (props.isLoadingGroupChat) {
    return <LoadIndicator height="1em" width="1em" />
  }

  return (
    <>
      {props.groupChat && (
        <div className="flex flex-col gap-2.5 p-2.5" ref={headPanelRef}>
          <div className="w-full flex gap-x-2.5 justify-between items-center @container">
            <div className="flex gap-2 items-center">
              {/* アバター */}
              {/* TODO: CREW-21003 実装後に正式に対応 */}
              <CrewAvatar
                shape="circle"
                displayName={props.groupChat.subject}
                size={CrewAvatarSize.lg}
                imageURL={generateImageAvatarUrl(EntityType.Project, projectId)}
                cacheValue={props.groupChat.id + props.groupChat.version}
              />

              {/* 表示名 */}
              <div
                className="text-2xl line-clamp-1 break-all"
                title={props.groupChat.subject}
              >
                {props.groupChat.subject}
              </div>
              {/* 通知アイコン */}
              <CrewNotificationProject projectId={props.groupChat.id} />

              {/* お気に入りアイコン */}
              <CrewFavorite
                key={props.groupChat.id}
                entityType={EntityType.Project}
                entityRecordId={props.groupChat.id}
                isFavorite={props.groupChat.projectFavorite ? true : false}
              />

              {/* メンバー */}
              <div className="flex shrink-0">
                <CrewGroupAvatar
                  groupAvatar={props.groupChat.projectMembers}
                  // ログイン中のユーザーがプロジェクトメンバーであり、プロジェクトメンバーを追加する権限を持っている場合に「ユーザー追加」ボタンを表示
                  showAddAvatar={
                    props.isJoinedGroupChat && hasPrjMemberAddPermission
                  }
                  onAddAvatarClick={openGroupChatAddMemberDialog}
                  limit={displayAvatarLimit}
                />
              </div>
            </div>

            {props.isJoinedGroupChat && (
              <div className="flex gap-x-1 shrink-0">
                <div className="flex gap-x-2.5">
                  {/* 編集ボタン */}
                  {hasPrjProjectEditPermission && (
                    <CrewButton
                      id="group-chat-head-panel-edit-button"
                      onClick={openGroupChatEditDialog}
                      text={t('action.edit')}
                      type="normal"
                      stylingMode="outlined"
                    />
                  )}

                  {/* 設定ボタン */}
                  {/* TODO: CrewButtonにMDIが適用できないので暫定対応でbootstrapで代用
                              アイコン付きボタンの実装についてはCREW-7493で対応
                              https://break-tmc.atlassian.net/browse/CREW-7493 */}
                  <div
                    id="group-chat-head-panel-settings-button-outer-div"
                    className="flex flex-row items-center"
                  >
                    <Menu
                      as="div"
                      id="group-chat-head-panel-settings-button-menu-div"
                      className="relative inline-block text-left"
                    >
                      <div>
                        <Menu.Button
                          id="group-chat-head-panel-settings-button-menu-button"
                          className="flex items-center rounded-md"
                        >
                          <CrewButton
                            id="group-chat-head-panel-settings-button-inner-button"
                            icon={<MoreHoriz width={20} height={20} />}
                            type="normal"
                            stylingMode="outlined"
                          />
                        </Menu.Button>
                      </div>

                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        {/* メニューの位置を固定し、一番上に表示する必要があるため、absoluteとz-10を指定している。 */}
                        <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md crew-bg-default crew-text-default shadow-lg ring-1 ring-crew-gray-200 dark:ring-crew-gray-700 focus:outline-none">
                          <div className="py-1">
                            <Menu.Item>
                              {/* グループから退出 */}
                              {({ active }) => (
                                <span
                                  className={classNames(
                                    { 'crew-bg-gray-2': active },
                                    'block px-4 py-2 text-sm',
                                    'cursor-pointer'
                                  )}
                                  onClick={handleLeaveGroupChatButtonClick}
                                >
                                  {t('label.leaveGroupChat')}
                                </span>
                              )}
                            </Menu.Item>
                          </div>
                        </Menu.Items>
                      </Transition>
                    </Menu>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* グループチャット編集ダイアログ */}
      {projectId && (
        <GroupChatEditDialog
          projectId={projectId}
          isOpen={isGroupChatEditDialogOpen}
          onSubmit={handleGroupChatUpdated}
          onClose={closeGroupChatEditDialog}
          onDeleted={props.onDeletedGroupChat}
        />
      )}
      {/* メンバー追加ダイアログ */}
      {projectId && (
        <GroupChatAddMemberDialog
          projectId={projectId}
          isOpen={isGroupChatAddMemberDialogOpen}
          onAddUsers={handleAddUsers}
          onClose={closeGroupChatAddMemberDialog}
        />
      )}
      {/* 確認ダイアログ */}
      <CrewConfirmDialog
        isOpen={isConfirmDialogOpen}
        message={confirmMessage}
        onPermitButtonClick={handleLeaveGroupChatPermitButtonClick}
        onCancelButtonClick={closeConfirmDialog}
        permitButtonDisabled={isLoadingLeaveGroupChat}
      />
    </>
  )
})
